/**
 * Typed Realtime Client for Linkode
 *
 * Creates a type-safe useRealtime hook using @upstash/realtime SDK.
 * This module provides the foundation for all realtime features.
 *
 * Note: SDK uses Zod v4 while our schemas use Zod v3, so we use
 * a simplified type approach to avoid compatibility issues.
 *
 * @module features/collab/transport/realtime-client
 * @see SPEC-007-6 Requirements 7.1-7.8
 */

'use client';

import { createRealtime } from '@upstash/realtime/client';
import type { RealtimeEventSchemaType } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * SDK connection status values
 * Includes both SDK native values ('open', 'closed') and legacy values ('connected', 'disconnected')
 */
export type ConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'error'
  | 'connecting'
  | 'reconnecting'
  | 'open'
  | 'closed';

/**
 * Event data payload from SDK
 */
export interface EventPayload {
  event: string;
  data: unknown;
  channel: string;
}

/**
 * Options for useRealtime hook
 */
export interface UseRealtimeOptions {
  events?: readonly string[];
  onData?: (payload: EventPayload) => void;
  channels?: readonly (string | undefined)[];
  enabled?: boolean;
}

/**
 * Return type for useRealtime hook
 */
export interface UseRealtimeResult {
  status: ConnectionStatus;
}

// ============================================================================
// SDK Integration
// ============================================================================

/**
 * Create typed realtime hooks
 * Using RealtimeEventSchemaType type from shared package for type safety
 */
const realtimeHooks = createRealtime<RealtimeEventSchemaType>();

/**
 * Typed useRealtime hook wrapper
 *
 * Wraps the SDK's useRealtime hook with our own types.
 * Provides type-safe event handling with proper narrowing.
 *
 * @example
 * ```typescript
 * const { status } = useRealtime({
 *   channels: ['doc:abc123'],
 *   events: ['presence.update', 'presence.join', 'presence.leave'],
 *   onData: ({ event, data, channel }) => {
 *     // Event narrowing based on event type
 *     if (event === 'presence.update') {
 *       const presenceData = data as PresenceUpdateEvent;
 *       console.log('Cursor:', presenceData.cursor);
 *     }
 *   },
 * });
 * ```
 */
export function useRealtime(opts: UseRealtimeOptions): UseRealtimeResult {
  const { status } = realtimeHooks.useRealtime({
    // Cast to any to avoid SDK type inference issues with Zod v3/v4 compatibility
    // Runtime validation is handled by Zod schema
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    events: opts.events as any,
    onData: opts.onData,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    channels: opts.channels as any,
    enabled: opts.enabled,
  });

  return { status: status as ConnectionStatus };
}

// Re-export types for convenience
export type { RealtimeEventSchemaType as RealtimeEvents };
